import { getProdutoComboText } from "@/utils/common/combo/combotext";
import { getCustomParams, getPaginationParams } from "@/utils/common/http";

import ProdutoModel, { ProdutoListParameters } from "../../models/produto/produtoModel";
import nfetch from "../../utils/events/nfetch";
import { iServiceCached } from "../base/iServiceCached";
import PDFService from "../pdf/PDFService";

class ProdutoService extends iServiceCached<ProdutoModel> {
    PDFService = new PDFService("");

    constructor() {
        super("Produto");
    }

    public listFilterProdutoTipo(tipoProduto: number, pageIndex: number, pageSize: number): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/ListFilterTipoProduto?TipoProduto=${tipoProduto}&pageIndex=${pageIndex}&pageSize=${pageSize}`,
            {
                credentials: "same-origin",
            },
        );
    }

    public getFromSearchComboByText(filterKey: string, estoqueId?: number): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/GetFromSearchComboByText?filterKey=${filterKey}&estoqueId=${estoqueId}`,
            {
                credentials: "same-origin",
            },
        );
    }

    public listIgnoreFilterProdutoTipo(pageIndex: number, pageSize: number, tipoProduto?: number): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/ListIgnoreFilterTipoProduto?pageIndex=${pageIndex}&pageSize=${pageSize}&TipoProduto=${tipoProduto}`,
            {
                credentials: "same-origin",
            },
        );
    }

    public replicar(idProduto: number, idFranquias: string, filtroVinculos: number): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/Replicar?idProduto=${idProduto}&idFranquias=${idFranquias}&filtroVinculos=${filtroVinculos}`,
            {
                method: "post",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json; charset=UTF-8",
                },
            },
        );
    }

    public getByParent(parentId: number, franquiaId: number): Promise<Response> {
        return nfetch(`/${this.controllerName}/GetByParentId?parentId=${parentId}&franquiaId=${franquiaId}`, {
            credentials: "same-origin",
        });
    }

    public atualizarValorVenda(): Promise<Response> {
        return nfetch(`/${this.controllerName}/AtualizarValorVenda`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
        });
    }

    public validateIncompatibilidades(produtosIds: number[], formaFarmaceuticaId: number): Promise<Response> {
        return nfetch(`/${this.controllerName}/ValidateIncompatibilidades?formaFarmaceuticaId=${formaFarmaceuticaId}`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
            body: JSON.stringify(produtosIds),
        });
    }

    public validateIncompatibilidadesKitEmbalagem(produtosIds: number[], kitEmbalagemId: number): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/ValidateIncompatibilidadesKitEmbalagem?kitEmbalagemId=${kitEmbalagemId}`,
            {
                method: "post",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json; charset=UTF-8",
                },
                body: JSON.stringify(produtosIds),
            },
        );
    }

    public getAliquotaICMSInterestadual(produtoId: number, ufDestino: string): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/GetAliquotaICMSInterestadual?produtoId=${produtoId}&ufDestino=${ufDestino}`,
            {
                credentials: "same-origin",
            },
        );
    }

    public getAliquotasICMSInterestadualDefault(): Promise<Response> {
        return nfetch(`/${this.controllerName}/GetAliquotasICMSInterestadualDefault`, {
            credentials: "same-origin",
        });
    }

    public getAliquotaInterestadualByNCM(ncm: string, produtoId: number, ufDestino: string): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/GetAliquotaInterestadualByNCM?ncm=${ncm}&produtoId=${produtoId}&ufDestino=${ufDestino}`,
            {
                credentials: "same-origin",
            },
        );
    }

    public getProdutosByIds(produtosIds: number[]): Promise<Response> {
        return nfetch(`/${this.controllerName}/GetProdutosByIds`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
            body: JSON.stringify(produtosIds),
        });
    }

    public getCamposDescricao(): Promise<Response> {
        return nfetch(`/${this.controllerName}/GetCampos`, {
            credentials: "same-origin",
        });
    }

    public hasProdutoControlado(produtoIds: number[]): Promise<Response> {
        return nfetch(`/${this.controllerName}/HasProdutoControlado`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
            body: JSON.stringify(produtoIds),
        });
    }

    public getById(id: number, quantidadeMinima = 0.1, tratarValores = true): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/GetById?id=${id}&quantidadeMinima=${quantidadeMinima}&tratarValores=${tratarValores}`,
            {
                credentials: "same-origin",
            },
        );
    }

    public getByIdFromManipulacao(id: number, quantidadeMinima = 0.1, tratarValores = true) {
        return nfetch(
            `/${this.controllerName}/GetByIdFromManipulacao?id=${id}&quantidadeMinima=${quantidadeMinima}&tratarValores=${tratarValores}`,
            {
                credentials: "same-origin",
            },
        );
    }

    public getByEstoqueId(estoqueId: number): Promise<Response> {
        return nfetch(`/${this.controllerName}/GetByEstoqueId?estoqueId=${estoqueId}`, {
            credentials: "same-origin",
        });
    }

    public comboOnlyAtivos() {
        const params: ProdutoListParameters = {
            somenteAtivos: true,
        };
        return this.list("", "descricao", "asc", 1, 999999, params);
    }

    public trocarUnidadeMedidaEstoque(produtoId: number, unidadeMedidaIdDestino: number): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/TrocarUnidadeMedidaEstoque?produtoId=${produtoId}&unidadeMedidaIdDestino=${unidadeMedidaIdDestino}`,
            {
                method: "post",
                credentials: "same-origin",
            },
        );
    }

    public calcularValorVenda(produto: ProdutoModel): Promise<Response> {
        return nfetch(`/${this.controllerName}/CalcularValorVenda`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
            body: JSON.stringify(produto),
        });
    }

    public calcularMarkupByValorVenda(produto: ProdutoModel): Promise<Response> {
        return nfetch(`/${this.controllerName}/CalcularMarkupByValorVenda`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
            body: JSON.stringify(produto),
        });
    }

    public updateValorVendaProdutosMarkupPadrao(): Promise<Response> {
        return nfetch(`/${this.controllerName}/UpdateValorVendaProdutosMarkupPadrao`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
        });
    }

    public exportPDF(
        filterKey: string,
        sortKey: string,
        sortOrder: string,
        customProperties: { [key: string]: any } = {},
    ) {
        const custom = getCustomParams(customProperties);
        const pagination = getPaginationParams(filterKey, sortKey, sortOrder, 0, 0);

        const url = `/${this.controllerName}/ExportPDF?${pagination}` + custom;

        this.PDFService.imprimirPDF(url);
    }

    public exportXLS(
        filterKey: string,
        sortKey: string,
        sortOrder: string,
        customProperties: { [key: string]: any } = {},
        tipoExportacaoXLS: number,
    ): Promise<Response> {
        const custom = getCustomParams(customProperties);
        const pagination = getPaginationParams(filterKey, sortKey, sortOrder, 0, 0);

        return nfetch(
            `/${this.controllerName}/ExportXLS?tipoExportacaoXLS=${tipoExportacaoXLS}&${pagination}` + custom,
            {
                method: "post",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json; charset=UTF-8",
                },
            },
        );
    }

    public hasProdutoUsoContinuoByIds(ids: number[]): Promise<Response> {
        return nfetch(`/${this.controllerName}/HasProdutoUsoContinuoByIds`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
            body: JSON.stringify(ids),
        });
    }

    public async comboCached(franquiaId: number, forceRefresh = false) {
        const retorno = await this.combo(franquiaId, forceRefresh);
        return retorno.map(p => ({
            id: p.id,
            value: p.id,
            text: getProdutoComboText(p),
            descricao: p.descricao,
            unidadeMedidaEstoqueId: p.unidadeMedidaEstoqueId,
            tipoProdutoId: p.tipoProduto,
            ativo: p.ativo,
            subGrupo: p.subGrupo,
            produtoPaiId: p.produtoPaiId,
            listaControladoId: p.listaControladoId,
        }));
    }

    public getFranquiaModeloCombo(): Promise<Response> {
        return nfetch(`/${this.controllerName}/GetFranquiaModeloCombo`, {
            credentials: "same-origin",
        });
    }
}

export default new ProdutoService();
